/**
 * Pipeline Metrics → one quality check, drilled down.
 *
 * The metrics table answers "how often did this fire"; this answers "on what".
 *
 * What a reader needs from a firing depends on the check. The DEFAULT view is a
 * flat table of firings — question, user, when, model, prompt, signals — which
 * is what you want for a check that is about the model or the prompt: scan the
 * rows, spot the model or version that produces them, open one.
 *
 * A check with its own review workflow gets its own view instead, registered in
 * `VIEW_BY_CHECK`. Today that is `objective_llm_answer_mismatch`, which is as
 * likely to mean the stored answer key is wrong as the model is, so it groups by
 * question and offers a per-question ruling — a flat run list cannot express
 * "this question, repeatedly, needs a decision".
 *
 * Window + source arrive as query params from the row that was clicked, so this
 * selects the same runs the number was computed from.
 */
import { useMemo, useState, type ReactElement } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BackButton, DataTable, SearchFilterBar,
  type ColumnDef, type FilterDef, type FilterOption, type SortState,
} from '@shared-ui/components';
import {
  adminApi,
  type QualityCheckDrilldown as QualityCheckDrilldownData,
  type QualityCheckQuestionGroup,
  type QualityCheckRun,
  type SuspectAnswerKeyFlag,
} from '../../api/adminApi';
import { CheckDescription, SeverityBadge } from './qualityCheckMeta';
import './Operations.css';

function fmtWhen(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
}

/** Signals are check-specific, so render whatever the check recorded rather
 *  than guessing at a schema. Numbers are rounded; objects are stringified. */
function fmtSignals(signals?: Record<string, unknown>): string {
  if (!signals || Object.keys(signals).length === 0) return '—';
  return Object.entries(signals)
    .map(([k, v]) => `${k}=${typeof v === 'number' ? Math.round(v * 100) / 100 : typeof v === 'object' ? JSON.stringify(v) : String(v)}`)
    .join('  ');
}

/** Only this check raises a suspect key, so only its drill-down shows one. */
const SUSPECT_KEY_CHECK = 'objective_llm_answer_mismatch';

/** Its own vocabulary, not the marking one: `correct`/`incorrect` mean whether a
 *  STUDENT was right, and reusing them for whether a REVIEWER accepted a finding
 *  would read as a verdict on the answer. */
const STATUS_CLASS: Record<SuspectAnswerKeyFlag['status'], string> = {
  open: 'status-open', accepted: 'status-accepted', dismissed: 'status-dismissed',
};

/**
 * The standing ruling, and the controls to make one.
 *
 * Deliberately shows `fireCount` and `firstSeenAt` next to the block's own
 * `fired`: the latter is scoped to the metrics window, so a key that has been
 * failing for two months but only twice this week reads as minor without them.
 */
function SuspectKeyPanel({ questionId, flag }: { questionId: string; flag: SuspectAnswerKeyFlag }) {
  const qc = useQueryClient();
  const rule = useMutation({
    mutationFn: (status: 'accepted' | 'dismissed' | 'open') =>
      adminApi.resolveSuspectAnswerKey(questionId, status),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['quality-check-drilldown'] }),
  });
  // A key ruled right releases the claims the flag held back. Ruled before
  // that existed (or skipped as stale), it is checked and released from here.
  const preview = useMutation({ mutationFn: () => adminApi.resolveSuspectAnswerKey(questionId, 'dismissed', true) });
  const released = rule.data?.released;
  const held = preview.data?.released;

  return (
    <div className="performance-section" style={{ margin: '8px 0', padding: 10 }}>
      <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', flexWrap: 'wrap' }}>
        <strong>Answer key</strong>
        <span className={`tag ${STATUS_CLASS[flag.status]}`}>{flag.status}</span>
        <span>
          stored <code>{flag.key}</code> · model computed <code>{flag.modelAnswers[0]}</code>
          {flag.modelAnswers.length > 1 && (
            <span className="muted"> (also {flag.modelAnswers.slice(1).join(', ')})</span>
          )}
        </span>
      </div>
      <p className="muted" style={{ margin: '6px 0' }}>
        {flag.fireCount} firing{flag.fireCount === 1 ? '' : 's'} all-time, first seen {fmtWhen(flag.firstSeenAt)}
        {flag.resolvedBy && ` · ruled by ${flag.resolvedBy} ${fmtWhen(flag.resolvedAt ?? '')}`}
      </p>
      <div className="suspect-key-actions" style={{ display: 'flex', gap: 8 }}>
        {flag.status === 'open' ? (
          <>
            <button type="button" className="save-btn" disabled={rule.isPending}
              onClick={() => rule.mutate('accepted')}>
              Key was wrong
            </button>
            <button type="button" className="save-btn" disabled={rule.isPending}
              onClick={() => rule.mutate('dismissed')}>
              Key is right
            </button>
          </>
        ) : (
          <>
            <button type="button" className="save-btn" disabled={rule.isPending}
              onClick={() => rule.mutate('open')}>
              Reopen
            </button>
            {flag.status === 'dismissed' && !held && (
              <button type="button" className="save-btn" disabled={preview.isPending}
                onClick={() => preview.mutate()}>
                {preview.isPending ? 'Checking…' : 'Check held-back claims'}
              </button>
            )}
            {flag.status === 'dismissed' && held && held.length > 0 && !released && (
              <button type="button" className="save-btn" disabled={rule.isPending}
                onClick={() => rule.mutate('dismissed')}>
                Release {held.length} submission{held.length === 1 ? '' : 's'}
              </button>
            )}
          </>
        )}
      </div>
      {held && !released && (
        <p className="muted" style={{ margin: '6px 0 0' }}>
          {held.length
            ? `Held back by this flag: ${held.map(r => `${r.submissionId} (${r.changes.map(c => c.claimType).join(', ')})`).join(' · ')}. Releasing re-grades the stored analysis — no AI call, the key and the marks unchanged.`
            : 'Nothing held back — no submission of this question carries the flag\'s reason.'}
        </p>
      )}
      {released && (
        <p className="muted" style={{ margin: '6px 0 0' }}>
          Released {released.filter(r => r.outcome === 'released').length} submission(s)
          {released.some(r => r.outcome === 'stale') && ` · ${released.filter(r => r.outcome === 'stale').length} changed meanwhile — check again`}.
        </p>
      )}
      {(rule.error ?? preview.error) && (
        <p className="error" style={{ margin: '6px 0 0' }}>{(rule.error ?? preview.error)!.message}</p>
      )}
      {/* Ruling records a decision; it never edits the key. Say so, or the
          buttons read as though "Key was wrong" fixes it. */}
      <p className="muted" style={{ marginTop: 6, fontSize: 12 }}>
        Recording a ruling does not change the answer — edit the question to correct it.
      </p>
      {rule.isError && <p className="error">Could not save the ruling.</p>}
    </div>
  );
}

function QuestionBlock({ group, onOpen }: { group: QualityCheckQuestionGroup; onOpen: (submissionId: string) => void }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="performance-section" style={{ marginBottom: 12 }}>
      <button
        type="button"
        className="qc-question-head"
        onClick={() => setOpen(o => !o)}
        style={{ display: 'flex', gap: 10, alignItems: 'baseline', width: '100%', textAlign: 'left', background: 'none', border: 0, cursor: 'pointer', color: 'inherit' }}
      >
        <span>{open ? '▾' : '▸'}</span>
        <span className="tag">{group.fired} fired</span>
        {group.subject && <span className="tag">{group.subject}</span>}
        {group.suspectAnswerKey && (
          <span className={`tag ${STATUS_CLASS[group.suspectAnswerKey.status]}`}>
            key {group.suspectAnswerKey.status}
          </span>
        )}
        <span style={{ flex: 1 }}>{group.questionText?.slice(0, 160) || group.questionId}</span>
      </button>

      {open && group.suspectAnswerKey && (
        <SuspectKeyPanel questionId={group.questionId} flag={group.suspectAnswerKey} />
      )}

      {open && (
        <table className="dt dt--compact" style={{ marginTop: 8 }}>
          <thead>
            <tr><th>When</th><th>Model</th><th>Prompt v</th><th>Primary</th><th>Signals</th><th>Analysis</th></tr>
          </thead>
          <tbody>
            {group.runs.map(r => (
              <tr key={r.outputId}>
                <td>{fmtWhen(r.generatedAt)}</td>
                <td>{r.model ?? '—'}</td>
                <td>{r.promptVersion ?? '—'}</td>
                <td>{r.isPrimary ? 'yes' : '—'}</td>
                <td style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>{fmtSignals(r.signals)}</td>
                <td>
                  {r.submissionId
                    ? <button type="button" className="link-button" onClick={() => onOpen(r.submissionId!)}>Open analysis →</button>
                    /* Eval-harness runs carry no submission, so there is nothing to open. */
                    : <span className="muted">—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

/** A firing, lifted out of its question group so it can be listed flat. */
type FlatRun = QualityCheckRun & { questionId: string; questionText: string; subject?: string };

function flatten(groups: QualityCheckQuestionGroup[]): FlatRun[] {
  return groups.flatMap(g => g.runs.map(r => ({
    ...r, questionId: g.questionId, questionText: g.questionText, subject: g.subject,
  })));
}

function sortRuns(rows: FlatRun[], sort: SortState): FlatRun[] {
  const dir = sort.sortOrder === 'asc' ? 1 : -1;
  const val = (r: FlatRun): string | number => {
    switch (sort.sortBy) {
      case 'question': return (r.questionText || r.questionId).toLowerCase();
      case 'user': return (r.userName || r.userId || '').toLowerCase();
      case 'model': return r.model ?? '';
      case 'prompt': return `${r.promptName ?? r.promptId ?? ''} ${r.promptVersion ?? 0}`.toLowerCase();
      case 'latencyMs': return r.latencyMs ?? -1;
      default: return r.generatedAt;
    }
  };
  return [...rows].sort((a, b) => {
    const av = val(a), bv = val(b);
    return (av < bv ? -1 : av > bv ? 1 : 0) * dir;
  });
}

function fmtDuration(ms?: number): string {
  if (ms === undefined) return '—';
  return ms < 1000 ? `${Math.round(ms)}ms` : `${(ms / 1000).toFixed(1)}s`;
}

/** Identity of a prompt+version pair. Ids, not names — the multi-selects
 *  comma-join their values, and a name is free text that could carry one. */
const promptKey = (r: FlatRun): string => `${r.promptId ?? ''}@${r.promptVersion ?? ''}`;
const promptLabel = (r: FlatRun): string =>
  `${r.promptName || r.promptId?.slice(0, 8) || '—'}${r.promptVersion !== undefined ? ` v${r.promptVersion}` : ''}`;

/** Slowest-first thresholds. A minimum rather than a band: the question these
 *  answer is "does this check only fire on slow runs", which a floor settles. */
const DURATION_FLOORS: FilterOption[] = [
  { value: '1000', label: '≥ 1s' },
  { value: '3000', label: '≥ 3s' },
  { value: '5000', label: '≥ 5s' },
  { value: '10000', label: '≥ 10s' },
  { value: '30000', label: '≥ 30s' },
];

/**
 * Filter options built from the rows actually present, so a select never offers
 * a model or prompt that would return nothing. Distinct values, first-seen order.
 */
function buildOptions(rows: FlatRun[]): Record<string, FilterOption[]> {
  const distinct = (
    keyOf: (r: FlatRun) => string | undefined,
    labelOf: (r: FlatRun) => string,
  ): FilterOption[] => {
    const seen = new Map<string, string>();
    for (const r of rows) {
      const k = keyOf(r);
      if (k && !seen.has(k)) seen.set(k, labelOf(r));
    }
    return [...seen].map(([value, label]) => ({ value, label }));
  };
  return {
    question: distinct(r => r.questionId, r => (r.questionText || r.questionId).slice(0, 80)),
    user: distinct(r => r.userId, r => r.userName || r.userId!.slice(0, 8)),
    model: distinct(r => r.model, r => r.model!),
    prompt: distinct(r => (r.promptId ? promptKey(r) : undefined), promptLabel),
  };
}

/** A blank/absent value means "no filter" for every dimension. */
function applyFilters(rows: FlatRun[], f: Record<string, string>): FlatRun[] {
  const anyOf = (csv: string | undefined): Set<string> | null => {
    const vals = (csv ?? '').split(',').filter(Boolean);
    return vals.length ? new Set(vals) : null;
  };
  const q = anyOf(f.question), u = anyOf(f.user), m = anyOf(f.model), p = anyOf(f.prompt);
  const floor = f.duration ? Number(f.duration) : undefined;
  return rows.filter(r => {
    if (q && !q.has(r.questionId)) return false;
    if (u && !(r.userId && u.has(r.userId))) return false;
    if (m && !(r.model && m.has(r.model))) return false;
    if (p && !p.has(promptKey(r))) return false;
    // A run with no recorded latency cannot satisfy a floor — excluding it is
    // honest, where treating "unknown" as fast would quietly pad the result.
    if (floor !== undefined && !(r.latencyMs !== undefined && r.latencyMs >= floor)) return false;
    return true;
  });
}

/**
 * Default view — every firing as one row, newest first.
 *
 * Sortable on each column because that is how the question gets answered: sort
 * by model and a model-specific failure stands out; sort by question and a
 * content problem clumps.
 */
function RunsView({ data }: DrilldownViewProps) {
  const navigate = useNavigate();
  const [sort, setSort] = useState<SortState>({ sortBy: 'generatedAt', sortOrder: 'desc' });
  const [filters, setFilters] = useState<Record<string, string>>({});

  // Filtering is client-side over the ≤500 rows already fetched: instant, and
  // it keeps the totals above reconciled with the metrics row that was clicked.
  const all = useMemo(() => flatten(data.questions), [data.questions]);
  const options = useMemo(() => buildOptions(all), [all]);
  const rows = useMemo(
    () => sortRuns(applyFilters(all, filters), sort),
    [all, filters, sort],
  );

  const filterDefs = useMemo<FilterDef[]>(() => [
    { key: 'question', kind: 'multiselect', placeholder: 'Question', options: options.question },
    { key: 'user', kind: 'multiselect', placeholder: 'User', options: options.user },
    { key: 'model', kind: 'multiselect', placeholder: 'Model', options: options.model },
    { key: 'prompt', kind: 'multiselect', placeholder: 'Prompt', options: options.prompt },
    { key: 'duration', placeholder: 'Any duration', options: DURATION_FLOORS },
  ], [options]);

  return (
    <div className="performance-section">
      <SearchFilterBar
        filters={filterDefs}
        values={filters}
        onChange={setFilters}
        hideSearch
        hideFetch
      />
      {rows.length !== all.length && (
        <p className="muted" style={{ margin: '6px 0' }}>
          {rows.length} of {all.length} firings match.
        </p>
      )}
      <DataTable
        compact
        rowKey={r => r.outputId}
        data={rows}
        sort={sort}
        onSortChange={setSort}
        emptyMessage="No firings in this window."
        columns={[
          {
            key: 'question',
            header: 'Question',
            sortable: true,
            render: r => (
              <span className="qc-question-cell" title={r.questionText || r.questionId}>
                {r.subject && <span className="tag">{r.subject}</span>}
                <span>{r.questionText || r.questionId}</span>
              </span>
            ),
          },
          {
            key: 'user',
            header: 'User',
            sortable: true,
            render: r => (r.userId
              ? <button type="button" className="link-button" onClick={() => navigate(`/users/${r.userId}`)}>
                  {r.userName || r.userId.slice(0, 8)}
                </button>
              /* Eval-harness runs have no user behind them. */
              : <span className="muted">—</span>),
          },
          { key: 'generatedAt', header: 'When', sortable: true, render: r => fmtWhen(r.generatedAt) },
          { key: 'model', header: 'Model', sortable: true, render: r => r.model ?? '—' },
          {
            key: 'prompt',
            header: 'Prompt',
            sortable: true,
            render: r => (
              <span title={r.promptId}>
                {r.promptName || r.promptId?.slice(0, 8) || '—'}
                {r.promptVersion !== undefined && <span className="muted"> v{r.promptVersion}</span>}
              </span>
            ),
          },
          { key: 'latencyMs', header: 'Duration', sortable: true, render: r => fmtDuration(r.latencyMs) },
          { key: 'signals', header: 'Signals', render: r => <span className="qc-signals">{fmtSignals(r.signals)}</span> },
          {
            key: 'analysis',
            header: '',
            render: r => (r.submissionId
              ? <button type="button" className="link-button" onClick={() => navigate(`/submissions/${r.submissionId}`)}>Open analysis →</button>
              : <span className="muted">—</span>),
          },
        ] as ColumnDef<FlatRun>[]}
      />
    </div>
  );
}

/**
 * `objective_llm_answer_mismatch` — grouped by question, with the standing
 * ruling on each question's answer key. A firing here is a question-level
 * finding someone has to decide on, not a run to skim.
 */
function AnswerKeyReviewView({ data }: DrilldownViewProps) {
  const navigate = useNavigate();
  if (data.questions.length === 0) return <div className="performance-section">No firings in this window.</div>;
  return (
    <>
      <p className="muted">
        A firing here means the model's working disagreed with the stored answer — rule on each below.
      </p>
      {data.questions.map(g => (
        <QuestionBlock key={g.questionId} group={g} onOpen={(id) => navigate(`/submissions/${id}`)} />
      ))}
    </>
  );
}

interface DrilldownViewProps { data: QualityCheckDrilldownData }

/** Checks whose firings need more than a run list. Everything else gets
 *  `RunsView`; add a check here only when the default genuinely cannot say what
 *  a reader has to do with the firing. */
const VIEW_BY_CHECK: Record<string, (props: DrilldownViewProps) => ReactElement> = {
  [SUSPECT_KEY_CHECK]: AnswerKeyReviewView,
};

export function QualityCheckDrilldown() {
  const { name = '' } = useParams();
  const [sp] = useSearchParams();
  const from = sp.get('from') ?? undefined;
  const to = sp.get('to') ?? undefined;
  const source = sp.get('source') ?? undefined;

  const { data, isLoading, error } = useQuery({
    queryKey: ['quality-check-drilldown', name, from, to, source],
    queryFn: () => adminApi.getQualityCheckDrilldown(name, { from, to, source }),
    enabled: !!name,
  });

  const totalRuns = useMemo(
    () => (data?.questions ?? []).reduce((n, q) => n + q.runs.length, 0),
    [data],
  );

  if (isLoading) return <div className="loading">Loading…</div>;
  if (error) return <div className="error">Failed to load drill-down for {name}</div>;
  if (!data) return null;

  const View = VIEW_BY_CHECK[data.name] ?? RunsView;

  return (
    <div className="operations-page">
      {/* fallback carries the tab so a deep link / refresh still lands on the
          metrics tab rather than the submissions default. */}
      <BackButton fallback="/submissions?tab=pipeline" label="Pipeline Metrics" />
      <h1 style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
        {data.name}
        <SeverityBadge name={data.name} />
      </h1>
      {/* The metrics table can only afford a tooltip; here there is room for the
          short line plus the mechanism, collapsed. */}
      <CheckDescription name={data.name} />
      <p className="muted">
        {data.fired.toLocaleString()} firing{data.fired === 1 ? '' : 's'} across {data.questions.length} question
        {data.questions.length === 1 ? '' : 's'} · {data.window.from} → {data.window.to} · {data.window.source}
      </p>
      {data.unattributed > 0 && (
        /* Model inputs age out, so an old firing can outlive the record that
           says which question it was about. Say so rather than quietly
           dropping it from the totals. */
        <p className="muted">{data.unattributed} firing(s) could not be attributed to a question (input no longer stored).</p>
      )}
      {totalRuns >= 500 && (
        <p className="muted">Showing the most recent 500 firings — narrow the range to see all of them.</p>
      )}

      <View data={data} />
    </div>
  );
}
